import { forwardRef, useImperativeHandle } from 'react';
import { IconEmailNew } from 'components/Icons';
import { useAuthContext } from 'context/useAuthContext';
import CMComposer from '../../Queue/CMComposer/CMComposer';
import { AskRequest, AskRequestCreateType, CreateAskRequestEmail } from '@hone-automation/common';
import { useCreateQueueRequestEmailMutation } from 'hooks/queue';
import { useQueueContext } from '../../Queue/QueueContext';
import KSCheckBox from 'presentation/components/KSCheckBox';
import KSInputField from 'presentation/components/KSInputField';
import { EmailAutocomplete } from 'presentation/components/EmailAutocomplete';
import { CMComposerContexts } from 'presentation/components/Queue/CMComposer/types';
import { CreateModal } from '../CreateModal';
import { Controller, useForm } from 'react-hook-form';

export interface CreateRequestEmailModalProps {
  isOpen: boolean;
  handleClose: () => void;
  resetState: () => void;
  locationName?: string;
  locationId?: string;
}

export interface CreateRequestEmailModalRef {
  resetState: () => void;
}

type FormValues = {
  subject: string;
  to: string;
  cc: string;
  isInternal: boolean;
};

const defaultValues: FormValues = {
  subject: '',
  to: '',
  cc: '',
  isInternal: false,
};

export const CreateRequestEmailModal = forwardRef<CreateRequestEmailModalRef, CreateRequestEmailModalProps>(
  (props, ref) => {
    const { isBookkeeperAdmin } = useAuthContext();
    const { dispatch } = useQueueContext();

    const locationName = props.locationName || 'Location Name';
    const locationId = props.locationId || '';

    const {
      control,
      reset,
      watch,
      trigger,
      formState: { errors },
    } = useForm<FormValues>({
      defaultValues,
      reValidateMode: 'onChange',
    });

    const isInternal = watch('isInternal');

    // Use the mutation hook
    const { mutate: createEmailRequest, isPending } = useCreateQueueRequestEmailMutation(
      locationId,
      (newRequest: AskRequest) => {
        dispatch({ type: 'SET_SELECTED_ITEM', payload: newRequest });
      }
    );

    const handleReset = () => {
      reset(defaultValues);
    };

    const handleSubjectChange = (onChange: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
      if (errors.subject) trigger('subject');
    };

    const handleToChange = (onChange: (v: string) => void) => (newValue: string) => {
      onChange(newValue);
      if (errors.to) trigger('to');
    };

    const handleCcChange = (onChange: (v: string) => void) => (newValue: string) => {
      onChange(newValue);
      trigger('cc');
    };

    const handleClose = () => {
      handleReset();
      props.handleClose();
    };

    useImperativeHandle(ref, () => ({
      resetState: handleReset,
    }));

    const handleSubmit = async (formData: FormData, _createType: AskRequestCreateType): Promise<void | false> => {
      const isValid = await trigger(['subject', 'to', 'cc']);
      if (!isValid) return false;

      const messageContent = (formData.get('comment') as string) || '';
      const formValues = watch();

      const ccEmails = formValues.cc
        .split(',')
        .map(email => email.trim())
        .filter(email => email.length > 0);

      const emailData: CreateAskRequestEmail = {
        subject: formValues.subject,
        to: formValues.to,
        cc: ccEmails,
        content: messageContent,
        visibility: formValues.isInternal ? 'Internal' : 'All',
        locationId,
      };

      createEmailRequest(emailData, {
        onSuccess: () => {
          handleClose();
        },
      });
    };

    return (
      <CreateModal
        isOpen={props.isOpen}
        onClose={handleClose}
        title="New Email"
        subtitle={locationName}
        icon={<IconEmailNew />}
        isInternal={isInternal}
        footer={
          <CMComposer
            handleOnCallback={handleSubmit}
            hideReopenLabel
            messageLoading={isPending}
            context={CMComposerContexts.createRequest}
            isCreateMode
            isInternal={isInternal}
            submitLabel="SEND"
          />
        }
      >
        <CreateModal.FormRow label="Subject" error={errors.subject?.message}>
          <Controller
            name="subject"
            control={control}
            rules={{ required: 'Subject is required' }}
            render={({ field: { value, onChange } }) => (
              <KSInputField
                type="text"
                value={value}
                placeholder="My new email"
                onChange={handleSubjectChange(onChange)}
              />
            )}
          />
        </CreateModal.FormRow>

        <CreateModal.FormRow label="To" htmlFor="to-input-field" error={errors.to?.message}>
          <Controller
            name="to"
            control={control}
            rules={{
              required: 'Recipient is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Enter a valid email address',
              },
            }}
            render={({ field: { value, onChange } }) => (
              <EmailAutocomplete
                value={value}
                onChange={handleToChange(onChange)}
                placeholder="kslocation@email.com"
                maxEmails={1}
              />
            )}
          />
        </CreateModal.FormRow>

        <CreateModal.FormRow label="CC" htmlFor="cc-input-field" error={errors.cc?.message}>
          <Controller
            name="cc"
            control={control}
            rules={{
              validate: value => {
                if (!value.trim()) return true;
                const invalid = value
                  .split(',')
                  .map(e => e.trim())
                  .filter(e => e.length > 0)
                  .filter(e => !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(e));
                return invalid.length === 0 || `Invalid email${invalid.length > 1 ? 's' : ''}: ${invalid.join(', ')}`;
              },
            }}
            render={({ field: { value, onChange } }) => (
              <EmailAutocomplete
                value={value}
                onChange={handleCcChange(onChange)}
                placeholder="email list separated by commas"
              />
            )}
          />
        </CreateModal.FormRow>

        {isBookkeeperAdmin && (
          <CreateModal.FormFrame>
            <Controller
              name="isInternal"
              control={control}
              render={({ field: { value, onChange } }) => (
                <KSCheckBox
                  rounded
                  label="Internal (Visible to KS Only)"
                  checked={value}
                  colorLabel="gray"
                  onChange={checked => onChange(checked)}
                />
              )}
            />
          </CreateModal.FormFrame>
        )}
      </CreateModal>
    );
  }
);

CreateRequestEmailModal.displayName = 'CreateRequestEmailModal';
